import { useTradeWayLabel } from 'components/secondaryMarket/shared/status/tradeWay';
import { ListGroup, ListGroupItem } from 'components/shared/ListGroup';
import { QuoteRepriceLink } from 'components/shared/QuoteRepriceLink';
import { useSPIntl } from 'hooks/useIntl';
import type { IntlKey } from 'locales';
import type { SideEnum } from 'models/secondaryMarket';
import { FC } from 'react';
import type { SecondaryQuotationType } from 'state/secondaryMarket/productSearch';

type Props = {
  quoteId: string;
  isin: string;
  quantity: number | undefined;
  quotationType: SecondaryQuotationType | undefined;
  tradeSide: SideEnum;
  cashAmount?: number;
  settlementDate?: string;
  accruedCoupon?: number;
  tradeDate?: string;
  upfrontFee?: number;
  deductedFee?: string;
  settlementPrice?: number;
  price?: number;
};
export const Details: FC<Props> = ({
  quoteId,
  isin,
  quantity,
  quotationType,
  tradeSide,
  cashAmount,
  settlementDate,
  accruedCoupon,
  tradeDate,
  upfrontFee,
  price,
  deductedFee,
}) => {
  const { formatDisplayedDate, formatNumber } = useSPIntl();
  const tradeWayLabel = useTradeWayLabel({ way: tradeSide });
  return (
    <ListGroup>
      <ListGroupItem className="ps-0 pe-3" labelKey="blotterPanel.externalQuoteId" data-e2e="quote-id">
        <QuoteRepriceLink quoteId={quoteId} externalQuoteId={quoteId} canCopy={false} disabled />
      </ListGroupItem>
      <ListGroupItem className="ps-0 pe-3" labelKey="secondaryMarket.productSummary.label.isin" data-e2e="isin">
        <span data-e2e="isin-value">{isin}</span>
      </ListGroupItem>
      <ListGroupItem
        className="ps-0 pe-3"
        labelKey="secondaryMarket.tradeInformation.label.quantity"
        data-e2e="quantity"
      >
        <span data-e2e="quantity-value">{quantity}</span>
      </ListGroupItem>
      <ListGroupItem
        className="ps-0 pe-3"
        labelKey="secondaryMarket.tradeInformation.tradeWay.label"
        data-e2e="tradeSide"
      >
        <span data-e2e="tradeSide-value">{tradeWayLabel}</span>
      </ListGroupItem>
      <ListGroupItem className="ps-0 pe-3" labelKey={mapQuotationTypeToPriceLabelIntlKey(quotationType!)} e2e="price">
        <span data-e2e="price-value">{formatNumber(price!)}</span>
      </ListGroupItem>
      <ListGroupItem
        className="ps-0 pe-3"
        labelKey="secondaryMarket.sidePanel.label.deductedFee"
        data-e2e="deductedFee"
      >
        <span data-e2e="deductedFee-value">{deductedFee}</span>
      </ListGroupItem>
      <ListGroupItem className="ps-0 pe-3" labelKey="secondaryMarket.sidePanel.label.upfrontFee" data-e2e="upfrontFee">
        <span data-e2e="upfrontFee-value">{upfrontFee}</span>
      </ListGroupItem>
      <ListGroupItem
        className="ps-0 pe-3"
        labelKey="secondaryMarket.productSummary.label.quotationType"
        data-e2e="quotationType"
      >
        <span data-e2e="quotationType-value">{quotationType}</span>
      </ListGroupItem>
      <ListGroupItem
        className="ps-0 pe-3"
        labelKey="secondaryMarket.sidePanel.label.accruedCoupon"
        data-e2e="accruedCoupon"
      >
        <span data-e2e="accruedCoupon-value">{accruedCoupon}</span>
      </ListGroupItem>
      <ListGroupItem className="ps-0 pe-3" labelKey="secondaryMarket.sidePanel.label.cashAmount" data-e2e="cashAmount">
        <span data-e2e="cashAmount-value">{formatNumber(cashAmount!)}</span>
      </ListGroupItem>
      <ListGroupItem className="ps-0 pe-3" labelKey="secondaryMarket.sidePanel.label.tradeDate" data-e2e="tradeDate">
        <span data-e2e="tradeDate-value">
          {formatDisplayedDate(tradeDate!, {
            day: '2-digit',
          })}
        </span>
      </ListGroupItem>
      <ListGroupItem
        className="ps-0 pe-3"
        labelKey="secondaryMarket.sidePanel.label.settlementDate"
        data-e2e="settlementDate"
      >
        <span data-e2e="settlementDate-value">
          {formatDisplayedDate(settlementDate!, {
            day: '2-digit',
          })}
        </span>
      </ListGroupItem>
    </ListGroup>
  );
};

function mapQuotationTypeToPriceLabelIntlKey(quotationType: SecondaryQuotationType): IntlKey {
  return quotationType === 'Clean'
    ? 'secondaryMarket.quotePanel.resume.label.cleanPrice'
    : 'secondaryMarket.quotePanel.resume.label.dirtyPrice';
}
